// THE declaration of the Cash Flow report (EVA-274). The order of this array IS
// spec §7; nothing else in the report knows about row order, section membership,
// borders, indentation or suppression policy.
//
// Adding a future cash-flow block is one entry here (two with a section heading)
// plus one i18n key. Do NOT reintroduce row-index arithmetic anywhere downstream.
//
// Field reference:
//   key           stable id — React key, expand state, spec assertions
//   kind          'section' | 'major' | 'minor'                        (§3 typography)
//   labelKey      i18n key under components.cash_flow_report
//   source        key into payload.data.rows                (absent for sections)
//   repeat        key into payload.data.rows holding [{id,label,values}] → N rows
//   always        true = §5 "never suppressed" structural anchor
//   suppressWith  'section' = §4 "suppressed together with the section"
//   rule          'none' | 'top' | 'strong'                  (§3 border treatment)
//   bold          §3 "Net Operating Income and Net Cash Flow should also be bold"
//   section       owning section, drives §5 section suppression
//   tenantSource  key into payload.data.rows holding [{id,title,values}]     (§6)

export const SECTIONS = {
  RENTAL: 'rental_revenue',
  REIMBURSEMENT: 'reimbursement_income',
  COLLECTION: 'collection_loss',
  OPEX: 'operating_expenses',
  LEASING: 'leasing_costs',
  CAPEX: 'capital_expenditure'
}

export const REPORT_SPEC = [
  /* §7.1  */ { key: 'sec_rental', kind: 'section', section: SECTIONS.RENTAL,
                labelKey: 'sections.rental_revenue', always: true },
  /* §7.2  */ { key: 'headline_rent', kind: 'major', source: 'headline_rent',
                labelKey: 'rows.headline_rent', always: true, rule: 'none',
                tenantSource: 'headline_rent_tenants' },
  /* §7.3  */ { key: 'void_deduction', kind: 'minor', section: SECTIONS.RENTAL,
                source: 'void_deduction', labelKey: 'rows.void_deduction',
                tenantSource: 'void_deduction_tenants' },
  /* §7.4  */ { key: 'rent_free_deduction', kind: 'minor', section: SECTIONS.RENTAL,
                source: 'rent_free_deduction', labelKey: 'rows.rent_free_deduction',
                tenantSource: 'rent_free_deduction_tenants' },
  /* §7.5  */ { key: 'current_rent_free_deduction', kind: 'minor', section: SECTIONS.RENTAL,
                source: 'current_rent_free_deduction', labelKey: 'rows.current_rent_free_deduction',
                tenantSource: 'current_rent_free_deduction_tenants' },
  /* §7.6  */ { key: 'passing_rent', kind: 'major', source: 'passing_rent',
                labelKey: 'rows.passing_rent', always: true, rule: 'top',
                tenantSource: 'passing_rent_tenants' },
  /* §7.7  */ { key: 'indexation', kind: 'minor', section: SECTIONS.RENTAL,
                source: 'indexation', labelKey: 'rows.indexation',
                tenantSource: 'indexation_tenants' },
  /* §7.8  */ { key: 'rent_free_indexation_adjustment', kind: 'minor', section: SECTIONS.RENTAL,
                source: 'rent_free_indexation_adjustment',
                labelKey: 'rows.rent_free_indexation_adjustment',
                tenantSource: 'rent_free_indexation_adjustment_tenants' },
  /* §7.9  */ { key: 'total_revenue', kind: 'major', source: 'total_revenue',
                labelKey: 'rows.total_revenue', always: true, rule: 'top',
                tenantSource: 'total_revenue_tenants' },
  /* §7.10 */ { key: 'sec_reimbursement', kind: 'section', section: SECTIONS.REIMBURSEMENT,
                labelKey: 'sections.reimbursement_income' },
  /* §7.11 */ { key: 'expense_recoveries', kind: 'minor', section: SECTIONS.REIMBURSEMENT,
                source: 'expense_recoveries', labelKey: 'rows.expense_recoveries',
                tenantSource: 'expense_recoveries_tenants' },
  /* §7.12 */ { key: 'potential_gross_revenue', kind: 'major', source: 'potential_gross_revenue',
                labelKey: 'rows.potential_gross_revenue', always: true, rule: 'top' },
  /* §7.13 */ { key: 'sec_collection', kind: 'section', section: SECTIONS.COLLECTION,
                labelKey: 'sections.collection_loss' },
  /* §7.14 */ { key: 'credit_loss', kind: 'minor', section: SECTIONS.COLLECTION,
                source: 'credit_loss', labelKey: 'rows.credit_loss',
                tenantSource: 'credit_loss_tenants' },
  /* §7.15 */ { key: 'effective_gross_revenue', kind: 'major', source: 'effective_gross_revenue',
                labelKey: 'rows.effective_gross_revenue', always: true, rule: 'top' },
  /* §7.16 */ { key: 'sec_opex', kind: 'section', section: SECTIONS.OPEX,
                labelKey: 'sections.operating_expenses' },
  /* §7.17 */ { key: 'opex_item', kind: 'minor', section: SECTIONS.OPEX,
                repeat: 'operating_expenses_items' },
  /* §7.18 */ { key: 'operating_expenses', kind: 'major', section: SECTIONS.OPEX,
                source: 'operating_expenses', labelKey: 'rows.operating_expenses',
                rule: 'top', suppressWith: 'section' },
  /* §7.19 */ { key: 'net_operating_income', kind: 'major', source: 'net_operating_income',
                labelKey: 'rows.net_operating_income', always: true, rule: 'strong', bold: true },
  /* §7.20 */ { key: 'sec_leasing', kind: 'section', section: SECTIONS.LEASING,
                labelKey: 'sections.leasing_costs' },
  // §7 orders Leasing Commissions BEFORE Tenant Improvements — the reverse of the
  // Audit tab (CashFlowTable.jsx renders TI first). The spec order wins here; the
  // divergence is these two lines, not a refactor.
  /* §7.21 */ { key: 'leasing_commissions', kind: 'minor', section: SECTIONS.LEASING,
                source: 'leasing_commissions', labelKey: 'rows.leasing_commissions',
                tenantSource: 'leasing_commissions_tenants' },
  /* §7.22 */ { key: 'tenant_improvements', kind: 'minor', section: SECTIONS.LEASING,
                source: 'tenant_improvements', labelKey: 'rows.tenant_improvements',
                tenantSource: 'tenant_improvements_tenants' },
  /* §7.23 */ { key: 'total_ti_and_lc', kind: 'major', section: SECTIONS.LEASING,
                source: 'total_ti_and_lc', labelKey: 'rows.total_ti_and_lc',
                rule: 'top', suppressWith: 'section', tenantSource: 'total_ti_and_lc_tenants' },
  /* §7.24 */ { key: 'sec_capex', kind: 'section', section: SECTIONS.CAPEX,
                labelKey: 'sections.capital_expenditure' },
  /* §7.25 */ { key: 'capex_item', kind: 'minor', section: SECTIONS.CAPEX,
                repeat: 'capital_expenditure_items' },
  /* §7.26 */ { key: 'capital_expenditure', kind: 'major', section: SECTIONS.CAPEX,
                source: 'capital_expenditure', labelKey: 'rows.capital_expenditure',
                rule: 'top', suppressWith: 'section' },
  /* §7.27 */ { key: 'net_cash_flow', kind: 'major', source: 'net_cash_flow',
                labelKey: 'rows.net_cash_flow', always: true, rule: 'strong', bold: true }
]

// Every payload key the report reads. Exported so a spec can assert the JSON
// contract still provides them all — a silently renamed key would otherwise read
// as an all-zero row, which suppression would then hide.
export const CONSUMED_ROW_KEYS = REPORT_SPEC
  .flatMap((s) => [s.source, s.repeat, s.tenantSource])
  .filter(Boolean)
